import React from 'react';
import { getEloTier } from '../utils/eloLogic';

// Simple SVG paths for Chess Pieces (Standard Vector)
const ICONS = {
    pawn: (
        <svg viewBox="0 0 45 45" className="w-full h-full fill-current">
            <path d="M22.5 9c-2.21 0-4 1.79-4 4 0 .89.29 1.71.78 2.38C17.33 16.5 16 18.59 16 21c0 2.03.94 3.84 2.41 5.03-3 1.06-7.41 5.55-7.41 13.47h23c0-7.92-4.41-12.41-7.41-13.47 1.47-1.19 2.41-3 2.41-5.03 0-2.41-1.33-4.5-3.28-5.62.49-.67.78-1.49.78-2.38 0-2.21-1.79-4-4-4z" />
        </svg>
    ),
    knight: (
        <svg viewBox="0 0 45 45" className="w-full h-full fill-current">
            <path d="M22 10c10.5 1 16.5 8 16 29H15c0-9 10-6.5 8-21" stroke="currentColor" strokeWidth="2" fill="none" />
            <path d="M24 18c.38 2.32-4.68 1.97-5 0 .32-2.31 4.62-1.98 5 0z" fill="currentColor" />
            {/* Simplified Silhouette */}
            <path d="M9 26c8.5-1.5 21-1.5 27 0l2-12s-7.36-4.56-11.5-4.5c-4.14.06-12 7-12 7S3.75 14 5 22c.76 4.88 4 4 4 4z" />
        </svg>
    ),
    // Wait, inline complex SVGs is messy. I will use standard Unicode/Icon paths wrapped nicely.
    // Actually, let's use a nice set of paths from Lucide if available or standard shapes.
    // Lucide doesn't have good chess icons. I will stick to these simplified SVGs or create better ones. 
    // Let's use standard Font Awesome style paths for robustness.

    // Re-doing with standard paths found in basic opensource sets

    pawn_simple: (
        <svg viewBox="0 0 24 24" fill="currentColor" className="w-full h-full">
            <path d="M12 2a2 2 0 1 1 0 4 2 2 0 0 1 0-4Zm0 6c-2.67 0-8 1.33-8 4v2h16v-2c0-2.67-5.33-4-8-4Zm-8 8v4h16v-4H4Z" />
        </svg>
    ),
    // Okay, writing raw SVG paths for all pieces is error-prone without visual verification.
    // I will use "Lucide" icons mapped to concepts as a safe fallback that looks elegant, 
    // OR I will trust my ability to produce "acceptable" paths.

    // User asked for "chess icons". I will do my best with standard paths.
};

const ChessIcon: React.FC<{ type: string, className?: string }> = ({ type, className }) => {
    // Shapes derived from common sets (like Phosphor or FontAwesome)
    switch (type) {
        case 'pawn':
            return (
                <svg viewBox="0 0 24 24" fill="currentColor" className={className}>
                    <path d="M12 2a3 3 0 0 1 3 3c0 1.25-.79 2.32-1.9 2.76A5.4 5.4 0 0 1 17 13v2H7v-2a5.4 5.4 0 0 1 3.9-5.24A3 3 0 0 1 9 5a3 3 0 0 1 3-3m-6 15v3h12v-3H6Z" />
                </svg>
            );
        case 'knight':
            return (
                <svg viewBox="0 0 24 24" fill="currentColor" className={className}>
                    <path d="M19 22H5v-2h14v2m-2-4H7c-1.1 0-2-.9-2-2v-5c0-2.21 1.79-4 4-4 .36 0 .72.04 1.07.13L12 3a3 3 0 0 1 3-3c1.31 0 2.41.83 2.83 2h.09A2.99 2.99 0 0 1 20.89 4c.69 1.19.78 2.66.22 3.93L19 12.33V16c0 1.11-.9 2-2 2Z" />
                </svg>
            );
        case 'bishop':
            return (
                <svg viewBox="0 0 24 24" fill="currentColor" className={className}>
                    <path d="M12 2c-.83 0-1.5.67-1.5 1.5S11.17 5 12 5s1.5-.67 1.5-1.5S12.83 2 12 2m0 3.5c-2.33 0-4.3 1.45-5.11 3.5h10.22A5.5 5.5 0 0 0 12 5.5m-5 5.5v2h2v-2H7m4 0v2h2v-2h-2m4 0v2h2v-2h-2M7 15v3h10v-3H7m-2 5v2h14v-2H5Z" />
                </svg>
            );
        case 'rook':
            return (
                <svg viewBox="0 0 24 24" fill="currentColor" className={className}>
                    <path d="M5 20v2h14v-2H5m0-9h14v7H5v-7m2-8h2v3h2V3h2v3h2V3h2v4H7V3Z" />
                </svg>
            );
        case 'king':
            return (
                <svg viewBox="0 0 24 24" fill="currentColor" className={className}>
                    <path d="M12 2a1 1 0 0 0-1 1v1.5H9.5a1 1 0 1 0 0 2H11v1.17a3.001 3.001 0 0 0-2.83 2.83H6.5a1 1 0 1 0 0 2h1.67v2.17c-1.35.43-2.17 1.83-1.95 3.33H17.78c.22-1.5-.6-2.9-1.95-3.33V12.5h1.67a1 1 0 1 0 0-2H15.83A3.001 3.001 0 0 0 13 9.67V8.5h1.5a1 1 0 1 0 0-2H13V3a1 1 0 0 0-1-1m-5 18v2h10v-2H7Z" />
                </svg>
            );
        default:
            return null;
    }
}

interface EloRankProps {
    rating: number;
    showLabel?: boolean;
    size?: 'sm' | 'md' | 'lg';
}

export const EloRank: React.FC<EloRankProps> = ({ rating, showLabel = true, size = 'md' }) => {
    const tier = getEloTier(rating);

    const sizeClasses = {
        sm: 'text-xs',
        md: 'text-base',
        lg: 'text-2xl'
    };

    const iconSize = {
        sm: 'w-4 h-4',
        md: 'w-6 h-6',
        lg: 'w-10 h-10'
    }

    return (
        <div className={`flex items-center gap-2 ${sizeClasses[size]}`} style={{ color: tier.color }}>
            <div className={`${iconSize[size]}`}>
                <ChessIcon type={tier.icon} className="w-full h-full" />
            </div>
            {showLabel && (
                <div className="font-serif tracking-widest uppercase font-bold">
                    <span className="opacity-80 font-sans text-[0.8em] mr-2">{rating}</span>
                    {tier.name}
                </div>
            )}
        </div>
    );
};
